/**
 * DeliveryChecklistWizard — full-screen 5-step wizard rendered when the DSE
 * clicks "Mark Delivered" (both standard and Early-Delivery paths).
 *
 * Design goals per spec:
 *   • One step at a time — Next is disabled until the current step passes.
 *   • Mobile-first — every button is at least 44px touch target; images use
 *     ``object-contain`` with capped heights per breakpoint.
 *   • VIN Verified + Documents-in-Yamaha-Bag are HARD gates. Any 'No' answer
 *     blocks completion (a reason can be captured but does not unlock Next).
 *   • Answers persist as the DSE navigates Back/Next.
 *   • Closing the wizard before completion does NOT mark the sale delivered.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { X, ChevronLeft, ChevronRight, CheckCircle2, XCircle, AlertTriangle, Maximize2, Star, Truck } from "lucide-react";

// Asset URLs (from Emergent asset library — do not inline base64).
const IMG_VIN     = "https://customer-assets.emergentagent.com/job_onedesk-sales/artifacts/f867dsw4_VIN%20Verification.png";
const IMG_NPS     = "https://customer-assets.emergentagent.com/job_onedesk-sales/artifacts/thlazda3_NPS%20Training.png";
const IMG_GOOGLE  = "https://customer-assets.emergentagent.com/job_onedesk-sales/artifacts/8ugynj6t_Google%20Rating%20.png";
const IMG_KIT     = "https://customer-assets.emergentagent.com/job_onedesk-sales/artifacts/b3spauyt_Documents.jpg";

const STEPS = ["VIN", "NPS", "Google Review", "Delivery Kit", "Confirm"];

function StepChip({ active, done, index }) {
  const cls = done
    ? "bg-emerald-500 text-white"
    : active
    ? "bg-primary text-white ring-2 ring-primary/30"
    : "bg-slate-200 text-slate-500";
  return (
    <div className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold sm:h-9 sm:w-9 sm:text-sm ${cls}`}>
      {done ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
    </div>
  );
}

function YesNo({ value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
      <button
        type="button"
        onClick={() => onChange(true)}
        data-testid={`${testidPrefix}-yes`}
        className={`flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-base font-semibold transition
          ${value === true
            ? "border-emerald-600 bg-emerald-50 text-emerald-700"
            : "border-slate-200 bg-white text-slate-700 hover:border-emerald-400"}`}
      >
        <CheckCircle2 className="h-5 w-5" />
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        data-testid={`${testidPrefix}-no`}
        className={`flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-base font-semibold transition
          ${value === false
            ? "border-red-600 bg-red-50 text-red-700"
            : "border-slate-200 bg-white text-slate-700 hover:border-red-400"}`}
      >
        <XCircle className="h-5 w-5" />
        No
      </button>
    </div>
  );
}

function StepShell({ title, badge, children }) {
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <div className="text-center">
        {badge && (
          <div className="mb-1 text-[11px] font-bold uppercase tracking-widest text-primary">{badge}</div>
        )}
        <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function DeliveryChecklistWizard({ open, sale, onClose, onComplete, googleReviewLink }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    vin_verified: null,
    nps_explained: null,
    google_review_opened: false,
    google_review_requested: null,
    documents_in_yamaha_bag: null,
    yamaha_bag_reason: "",
  });
  const [kitPreviewOpen, setKitPreviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const vin = (sale?.vehicle?.vin || "").toString();
  const invoiceVin = (sale?.accounts?.invoice_vin || sale?.vehicle?.vin || "").toString();
  const modelName = sale?.vehicle?.model || "";
  const custName = sale?.customer?.name || "";
  const invoiceNum = sale?.accounts?.invoice_number || "";
  const dseName = sale?._dse_name || "";
  const locName = sale?._location_name || "";
  const reviewUrl = googleReviewLink || "https://www.google.com/search?q=ivamotors+yamaha";

  const stepValid = useMemo(() => {
    switch (step) {
      case 0: return answers.vin_verified === true;
      case 1: return answers.nps_explained === true;
      case 2: return answers.google_review_opened && answers.google_review_requested === true;
      case 3: return answers.documents_in_yamaha_bag === true;
      case 4: return true;
      default: return false;
    }
  }, [step, answers]);

  const setAns = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await onComplete({
        vin_verified: !!answers.vin_verified,
        nps_explained: !!answers.nps_explained,
        google_review_requested: !!answers.google_review_requested,
        documents_in_yamaha_bag: !!answers.documents_in_yamaha_bag,
        yamaha_bag_reason: answers.yamaha_bag_reason || null,
      });
    } catch (e) {
      toast.error(e?.message || "Failed to complete delivery");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-white" data-testid="delivery-checklist-wizard">
      <div className="mx-auto flex h-full max-w-3xl flex-col">
        {/* ── Top bar with title + progress ─────────────────────────── */}
        <div className="flex items-start justify-between border-b border-slate-100 px-4 pb-3 pt-4 sm:px-6">
          <div>
            <div className="font-display text-base font-bold text-slate-900 sm:text-lg">
              Delivery Completion Checklist
            </div>
            <div className="text-xs text-slate-500">
              Step {step + 1} of {STEPS.length} · {STEPS[step]}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="dcw-close"
            className="grid h-9 w-9 place-items-center rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="border-b border-slate-100 px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between gap-1">
            {STEPS.map((_, i) => (
              <React.Fragment key={i}>
                <StepChip index={i} active={i === step} done={i < step} />
                {i < STEPS.length - 1 && (
                  <div className={`h-1 flex-1 rounded ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Step body (scrollable) ──────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6" data-testid={`dcw-step-${step}`}>
          {step === 0 && (
            <StepShell badge="Step 1 · VIN Verification" title="VIN Verification">
              <img
                src={IMG_VIN}
                alt="VIN verification"
                className="mx-auto max-h-[260px] w-full rounded-xl object-contain sm:max-h-[320px]"
              />
              <div className="text-center text-sm font-medium text-slate-700">
                Vehicle VIN and Billed Invoice VIN Verified?
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="mb-1"><span className="font-semibold text-slate-500">Vehicle VIN:</span> <span className="font-mono-data font-bold text-slate-900">{vin || "—"}</span></div>
                <div><span className="font-semibold text-slate-500">Invoice VIN:</span> <span className="font-mono-data font-bold text-slate-900">{invoiceVin || "—"}</span></div>
              </div>
              <YesNo value={answers.vin_verified} onChange={(v) => setAns("vin_verified", v)} testidPrefix="dcw-vin" />
              {answers.vin_verified === false && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div><b>Please stop the delivery</b> and contact Accounts / PDI for correction. VIN verification is mandatory.</div>
                </div>
              )}
            </StepShell>
          )}
          {step === 1 && (
            <StepShell badge="Step 2 · NPS Explanation" title="NPS Explanation">
              <img
                src={IMG_NPS}
                alt="NPS explanation"
                className="mx-auto max-h-[220px] w-full rounded-xl object-contain sm:max-h-[280px]"
              />
              <div className="text-center text-sm font-medium text-slate-700">
                NPS Explained to Customer?
              </div>
              <YesNo value={answers.nps_explained} onChange={(v) => setAns("nps_explained", v)} testidPrefix="dcw-nps" />
              {answers.nps_explained !== true && (
                <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                  Explain the Yamaha NPS feedback process to the customer before proceeding.
                </div>
              )}
            </StepShell>
          )}
          {step === 2 && (
            <StepShell badge="Step 3 · Google Review" title="Google Review">
              <img
                src={IMG_GOOGLE}
                alt="Google Review"
                className="mx-auto max-h-[180px] w-full rounded-xl object-contain sm:max-h-[220px]"
              />
              <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                Request the customer to provide a Google Review for the dealership.
              </div>
              <Button
                type="button"
                onClick={() => { window.open(reviewUrl, "_blank", "noopener,noreferrer"); setAns("google_review_opened", true); }}
                className="min-h-[52px] w-full gap-2 bg-amber-500 text-base font-bold text-white hover:bg-amber-600"
                data-testid="dcw-google-open"
              >
                <Star className="h-5 w-5 fill-white" />
                Take Google Review
              </Button>
              {answers.google_review_opened && (
                <>
                  <div className="text-center text-sm font-medium text-slate-700 pt-1">
                    Google Review Requested?
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                    <button
                      type="button"
                      onClick={() => setAns("google_review_requested", true)}
                      data-testid="dcw-google-done"
                      className={`min-h-[48px] flex-1 rounded-xl border-2 px-4 py-3 text-base font-semibold ${
                        answers.google_review_requested === true
                          ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-white text-slate-700"
                      }`}
                    >Done</button>
                    <button
                      type="button"
                      onClick={() => setAns("google_review_requested", false)}
                      data-testid="dcw-google-notdone"
                      className={`min-h-[48px] flex-1 rounded-xl border-2 px-4 py-3 text-base font-semibold ${
                        answers.google_review_requested === false
                          ? "border-red-600 bg-red-50 text-red-700"
                          : "border-slate-200 bg-white text-slate-700"
                      }`}
                    >Not Done</button>
                  </div>
                </>
              )}
            </StepShell>
          )}
          {step === 3 && (
            <StepShell badge="Step 4 · Delivery Kit Verification" title="Delivery Kit Verification">
              <img
                src={IMG_KIT}
                alt="Yamaha Delivery Folder and Bag"
                className="mx-auto w-full rounded-xl object-contain"
                style={{ maxHeight: "min(300px, 32vh)" }}
              />
              <div className="flex justify-center">
                <Button type="button" variant="outline" onClick={() => setKitPreviewOpen(true)}
                  className="gap-1.5" data-testid="dcw-kit-full">
                  <Maximize2 className="h-4 w-4" /> View Full Image
                </Button>
              </div>
              <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
                <b>All documents must be handed over inside the official Yamaha Delivery Folder and Yamaha Bag.</b>
                Do not hand over loose documents or use an envelope.
              </div>
              <div className="text-center text-sm font-medium text-slate-700">
                All documents handed over in Yamaha Bag?
              </div>
              <YesNo value={answers.documents_in_yamaha_bag} onChange={(v) => setAns("documents_in_yamaha_bag", v)} testidPrefix="dcw-bag" />
              {answers.documents_in_yamaha_bag === false && (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Reason for not handing over in Yamaha Bag <span className="text-red-600">*</span>
                  </label>
                  <Textarea
                    value={answers.yamaha_bag_reason || ""}
                    onChange={(e) => setAns("yamaha_bag_reason", e.target.value)}
                    placeholder="Please explain why…"
                    className="min-h-[80px]"
                    data-testid="dcw-bag-reason"
                  />
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>You cannot complete the delivery. Select <b>Yes</b> after handing over in the Yamaha Bag to proceed.</div>
                  </div>
                </div>
              )}
            </StepShell>
          )}
          {step === 4 && (
            <StepShell badge="Step 5 · Final Confirmation" title="Confirm Delivery Completion">
              <div className="space-y-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                {[
                  "VIN and Invoice VIN Verified",
                  "NPS Explained",
                  "Google Review Requested",
                  "Documents handed over in Yamaha Bag",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> {t}</div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
                {[
                  ["Customer", custName], ["Model", modelName],
                  ["VIN", vin || "—"], ["Invoice Number", invoiceNum || "—"],
                  ["DSE", dseName || "—"], ["Location", locName || "—"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{k}</div>
                    <div className="font-mono-data font-semibold text-slate-900">{v}</div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="min-h-[56px] w-full gap-2 bg-emerald-600 text-lg font-bold hover:bg-emerald-700"
                data-testid="dcw-complete"
              >
                <Truck className="h-5 w-5" />
                {submitting ? "Completing…" : "Complete Delivery"}
              </Button>
            </StepShell>
          )}
        </div>

        {/* ── Footer nav ─────────────────────────────────────────── */}
        {step < 4 && (
          <div className="sticky bottom-0 border-t border-slate-100 bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
            <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
              {step === 0 ? (
                <Button type="button" variant="outline" onClick={onClose}
                  className="min-h-[48px] gap-1.5" data-testid="dcw-cancel">
                  Cancel Delivery
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="min-h-[48px] gap-1.5" data-testid="dcw-back">
                  <ChevronLeft className="h-4 w-4" /> Back
                </Button>
              )}
              <Button
                type="button"
                onClick={() => setStep((s) => Math.min(4, s + 1))}
                disabled={!stepValid}
                className="min-h-[48px] gap-1.5"
                data-testid="dcw-next"
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Full-screen kit image preview ───────────────────────── */}
      {kitPreviewOpen && (
        <div className="fixed inset-0 z-[110] flex flex-col bg-black/90" onClick={() => setKitPreviewOpen(false)}
             data-testid="dcw-kit-preview">
          <button
            type="button"
            className="ml-auto m-3 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-slate-800"
            onClick={(e) => { e.stopPropagation(); setKitPreviewOpen(false); }}
            aria-label="Close preview"
            data-testid="dcw-kit-preview-close"
          >
            <X className="h-5 w-5" />
          </button>
          <img src={IMG_KIT} alt="Yamaha Delivery Folder and Bag full"
               className="mx-auto max-h-full max-w-full flex-1 object-contain p-4" />
        </div>
      )}
    </div>
  );
}
